"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { useEffect, useState } from "react";
import clsx from "clsx";
import { useTheme } from "./theme/ThemeProvider";
import { Sun, Moon, ChevronDown } from "lucide-react";

const NAV_STRUCTURE = [
  {
    label: "Home",
    href: "/",
    dropdown: null
  },
  {
    label: "About",
    href: null,
    dropdown: [
      { href: "/manifesto", label: "Manifesto" },
      { href: "/foundation", label: "Foundation" },
    ]
  },
  {
    label: "Tech",
    href: null,
    dropdown: [
      { href: "/technology", label: "Technology" },
      { href: "/integrations", label: "Integrations" },
    ]
  },
  {
    label: "Ecosystem",
    href: null,
    dropdown: [
      { href: "/economy", label: "Economy" },
      { href: "/game", label: "Game" },
      { href: "/community", label: "Community" },
    ]
  },
  {
    label: "Explorer",
    href: "/explorer",
    dropdown: null
  },
];

export default function Navbar() {
  const pathname = usePathname();
  const { theme, setTheme } = useTheme();
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const toggleTheme = () => {
    setTheme(theme === "dark" ? "light" : "dark");
  };

  return (
    <header className={clsx("navbar", scrolled && "scrolled")} data-theme-context={theme}>
      <div className="navbar__inner">
        {/* Logo - Izquierda */}
        <Link href="/" className="navbar__logo flex items-center gap-3">
          <img
            src="/phytoncoin_logo.png"
            alt="PhytonCoin Logo"
            width={48}
            height={48}
            className="logo-glow"
          />
          <div className="leading-tight">
            <span className="block text-xs uppercase tracking-[0.28em] text-primary font-semibold">
              Proof of Action
            </span>
            <span className="block text-lg font-bold" style={{ color: scrolled ? "var(--text)" : "#ffffff" }}>
              PhytonCoin
            </span>
          </div>
        </Link>

        {/* Links - Centro con Mega Dropdown */}
        <nav className="navbar__links">
          {NAV_STRUCTURE.map((item) => {
            if (item.dropdown) {
              // Item con dropdown
              return (
                <div key={item.label} className="nav-dropdown">
                  <button className="nav-link nav-link--dropdown" style={{ color: !scrolled ? "#ffffff" : undefined }}>
                    {item.label}
                    <ChevronDown className="w-4 h-4 ml-1" />
                  </button>
                  <div className="nav-dropdown__menu">
                    {item.dropdown.map((subItem) => {
                      const active = pathname === subItem.href;
                      return (
                        <Link
                          key={subItem.href}
                          href={subItem.href}
                          className={clsx("nav-dropdown__item", active && "nav-dropdown__item--active")}
                        >
                          {subItem.label}
                        </Link>
                      );
                    })}
                  </div>
                </div>
              );
            } else {
              // Link simple
              const active = pathname === item.href || (item.href !== "/" && pathname.startsWith(item.href!));
              return (
                <Link
                  key={item.label}
                  href={item.href!}
                  className={clsx("nav-link", active && "nav-link--active")}
                  style={{ color: !scrolled && !active ? "#ffffff" : undefined }}
                  aria-current={active ? "page" : undefined}
                >
                  {item.label}
                </Link>
              );
            }
          })}
        </nav>

        {/* Acciones - Derecha */}
        <div className="navbar__actions">
          <button
            onClick={toggleTheme}
            className="p-2 rounded-full transition-all duration-300 hover:scale-110"
            style={{
              background: scrolled ? "rgba(30, 166, 122, 0.1)" : "rgba(255, 255, 255, 0.1)",
              backdropFilter: "blur(10px)",
            }}
            aria-label="Toggle theme"
          >
            {theme === "dark" ? (
              <Sun className="w-5 h-5" style={{ color: scrolled ? "var(--primary)" : "#ffffff" }} />
            ) : (
              <Moon className="w-5 h-5" style={{ color: scrolled ? "var(--primary)" : "#ffffff" }} />
            )}
          </button>
        </div>
      </div>
    </header>
  );
}
